import { useCallback } from 'react';
import { prop } from 'ramda';
import { useDispatch } from 'react-redux';
import { useTranslations } from 'js/hooks/i18n';
import { openNewWindow } from 'js/utils/url.util';
import { useDeviceSelector } from 'js/store/hooks';
import { userSetAttributesAction } from 'js/hooks/user/store/actions';
import { useSmartClosePopups } from 'js/components/common/PopupManager';
import { publicEventsAPIService } from 'js/services/publicEventsAPI.service';
import { showCommonPopupAction } from 'js/components/common/popups/CommonPopup';

import * as Types from '../types';
import { GAME_LAUNCH_METHODS } from '../constants';
import { useGetGameConfig } from './useGetGameConfig';
import { useGetLaunchMethod } from './useGetLaunchMethod';
import { useLaunchByRedirect } from './useLaunchByRedirect';
import { useLaunchInNewWindow } from './useLaunchInNewWindow';
import { useGetWrapperLaunchUrl } from './useGetWrapperLaunchUrl';
import { useGetRedirectLaunchUrl } from './useGetRedirectLaunchUrl';
import { useNavigateFromLaunchGamePath } from './useNavigateFromLaunchGamePath';

export const useOpenGame = () => {
    const dispatch = useDispatch();
    const closePopups = useSmartClosePopups();
    const isDesktop = useDeviceSelector(prop('isDesktop'));
    const translate = useTranslations('playtech.mobile.system.launcher.gameUnavailablePopup.');
    const navigateFromLaunchGamePath = useNavigateFromLaunchGamePath();
    const getGameConfig = useGetGameConfig();
    const getWrapperLaunchUrl = useGetWrapperLaunchUrl();
    const getRedirectLaunchUrl = useGetRedirectLaunchUrl();
    const getLaunchMethod = useGetLaunchMethod();
    const launchByRedirect = useLaunchByRedirect();
    const launchInNewWindow = useLaunchInNewWindow();

    return useCallback(({ gameCode, mode, additionalQueryString, ...options }: Types.ILaunchGameData): void => {
        const gameConfig = getGameConfig(gameCode);
        const launchMethod = getLaunchMethod({ gameCode });
        const gameWindow = launchMethod === GAME_LAUNCH_METHODS.REDIRECT_IN_NEW_WINDOW
            ? openNewWindow() : null;
        const isLaunchInWrapper = launchMethod && [
            GAME_LAUNCH_METHODS.WRAPPER_IN_NEW_WINDOW,
            GAME_LAUNCH_METHODS.WRAPPER_IN_THIS_WINDOW
        ].includes(launchMethod);

        if (!gameConfig) {
            return;
        }

        closePopups('gameLaunch');
        dispatch(userSetAttributesAction({
            launchedGame: {
                code: gameCode,
                mode,
                queryString: additionalQueryString,
                ...options
            }
        }));

        (async () => {
            try {
                const launcherUrl = isLaunchInWrapper
                    ? getWrapperLaunchUrl({ gameCode, mode, additionalQueryString })
                    : await getRedirectLaunchUrl({ gameCode, mode, additionalQueryString });

                if (!launcherUrl) {
                    return;
                }

                switch (launchMethod) {
                    case GAME_LAUNCH_METHODS.REDIRECT_IN_NEW_WINDOW:
                    case GAME_LAUNCH_METHODS.WRAPPER_IN_NEW_WINDOW: {
                        navigateFromLaunchGamePath();

                        launchInNewWindow({ launcherUrl, gameWindow });

                        break;
                    }
                    case GAME_LAUNCH_METHODS.REDIRECT_IN_THIS_WINDOW:
                    case GAME_LAUNCH_METHODS.WRAPPER_IN_THIS_WINDOW:
                    default:
                        launchByRedirect({ launcherUrl });
                        break;
                }

                options.onGameLaunched?.();
                // for game launch tracking
                publicEventsAPIService.trigger('game-launch-event', {
                    mode,
                    options,
                    game: gameConfig
                });
            } catch {
                gameWindow?.close();

                dispatch(showCommonPopupAction({
                    title: `${gameConfig.name} ${translate('title')}`,
                    content: translate(isDesktop ? 'desktop-content' : 'mobile-content'),
                    onClose: () => navigateFromLaunchGamePath()
                }));
            }
        })();
    }, [
        dispatch, getRedirectLaunchUrl, translate, getLaunchMethod, getGameConfig,
        closePopups, isDesktop, launchByRedirect, launchInNewWindow,
        getWrapperLaunchUrl, navigateFromLaunchGamePath
    ]);
};
